import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FaSpinner, FaEye, FaEyeSlash, FaShieldAlt, FaQrcode, FaMobileAlt } from 'react-icons/fa';

export default function AdminLogin() {
  const [phase, setPhase] = useState('credentials'); // 'credentials' | 'setup' | 'totp'
  const [form, setForm] = useState({ username: '', password: '' });
  const [tempToken, setTempToken] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleCredentials = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await axios.post('/api/auth/login', form);
      setTempToken(data.tempToken);
      if (data.phase === 'setup') {
        setQrUrl(data.qrUrl);
        setSecret(data.secret);
        setPhase('setup');
      } else {
        setPhase('totp');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Giriş başarısız');
    }
    setLoading(false);
  };

  const handleTotp = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await axios.post('/api/auth/verify-totp', { tempToken, code: totpCode });
      login(data.token, data.username);
      navigate('/yonetim/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Kod hatalı');
      setTotpCode('');
    }
    setLoading(false);
  };

  const goBack = () => { setPhase('credentials'); setTotpCode(''); setError(''); };

  return (
    <div className="min-h-screen hero-gradient hero-pattern flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-primary px-8 py-8 text-center">
            <div className="w-16 h-16 bg-secondary rounded-xl flex items-center justify-center mx-auto mb-3">
              {phase === 'credentials' ? (
                <FaShieldAlt className="text-white text-3xl" />
              ) : phase === 'setup' ? (
                <FaQrcode className="text-white text-3xl" />
              ) : (
                <FaMobileAlt className="text-white text-3xl" />
              )}
            </div>
            <h1 className="text-white text-xl font-bold">
              {phase === 'credentials' && 'Admin Paneli'}
              {phase === 'setup' && 'Google Authenticator Kurulumu'}
              {phase === 'totp' && 'İki Faktörlü Doğrulama'}
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {phase === 'credentials' && 'Yönetim sistemine giriş yapın'}
              {phase === 'setup' && 'İlk girişte tek seferlik kurulum gereklidir'}
              {phase === 'totp' && 'Authenticator uygulamasındaki kodu girin'}
            </p>
          </div>

          {/* Phase 1: Credentials */}
          {phase === 'credentials' && (
            <form onSubmit={handleCredentials} className="px-8 py-8 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kullanıcı Adı</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  required
                  className="input-field"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Şifre</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required
                    className="input-field pr-11"
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 text-sm">{error}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3.5 disabled:opacity-60">
                {loading ? <><FaSpinner className="animate-spin" /> Doğrulanıyor...</> : 'Devam Et →'}
              </button>
            </form>
          )}

          {/* Phase 2: TOTP Setup (first login or after reset) */}
          {phase === 'setup' && (
            <form onSubmit={handleTotp} className="px-8 py-8 space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                <p className="font-semibold mb-2">Nasıl kurulur?</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Telefonunuzda <strong>Google Authenticator</strong> uygulamasını açın</li>
                  <li>"+" → "QR kodu tara" seçeneğine dokunun</li>
                  <li>Aşağıdaki QR kodu kameranıza gösterin</li>
                  <li>Oluşan <strong>6 haneli kodu</strong> girin ve kaydedin</li>
                </ol>
              </div>

              {qrUrl && (
                <div className="flex justify-center">
                  <div className="bg-white border-2 border-gray-200 rounded-2xl p-3 inline-block">
                    <img src={qrUrl} alt="QR Kod" className="w-44 h-44" />
                  </div>
                </div>
              )}

              <details className="group">
                <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                  QR kod taranamıyorsa manuel giriş kodu
                </summary>
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <code className="text-xs font-mono text-gray-600 break-all">{secret}</code>
                </div>
              </details>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Doğrulama Kodu
                  <span className="text-gray-400 font-normal ml-1">(uygulamadan 6 hane)</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="input-field text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  autoFocus
                />
              </div>

              {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 text-sm">{error}</div>}

              <button type="submit" disabled={loading || totpCode.length < 6}
                className="btn-primary w-full justify-center py-3.5 disabled:opacity-60">
                {loading ? <><FaSpinner className="animate-spin" /> Doğrulanıyor...</> : 'Kurulumu Tamamla'}
              </button>
              <button type="button" onClick={goBack}
                className="w-full text-center text-sm text-gray-400 hover:text-gray-600 py-1">
                ← Geri dön
              </button>
            </form>
          )}

          {/* Phase 3: TOTP Verify (normal login) */}
          {phase === 'totp' && (
            <form onSubmit={handleTotp} className="px-8 py-8 space-y-5">
              <p className="text-sm text-gray-500 text-center">
                Google Authenticator uygulamasını açarak <strong>Polikap Admin</strong> için oluşturulan kodu girin.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Doğrulama Kodu
                  <span className="text-gray-400 font-normal ml-1">(6 hane)</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="input-field text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  autoFocus
                />
              </div>

              {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 text-sm">{error}</div>}

              <button type="submit" disabled={loading || totpCode.length < 6}
                className="btn-primary w-full justify-center py-3.5 disabled:opacity-60">
                {loading ? <><FaSpinner className="animate-spin" /> Doğrulanıyor...</> : 'Giriş Yap'}
              </button>
              <button type="button" onClick={goBack}
                className="w-full text-center text-sm text-gray-400 hover:text-gray-600 py-1">
                ← Geri dön
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
